/**
 * PATH: packages/email-hub/src/feature/components/rich-text/slash.tsx
 * FILE_ID: EMAILHUB-RICHTEXT-SLASH-001
 * PURPOSE: "/" slash command menu — insert headings, lists, quote, divider,
 *          table, image, or a Kanban card chip from the keyboard. Image and
 *          card open their pickers via an `onAction` callback.
 */

import { Extension } from '@tiptap/core'
import type { Editor, Range } from '@tiptap/core'
import { Suggestion } from '@tiptap/suggestion'
import {
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Quote,
  Minus,
  Table as TableIcon,
  Image as ImageIcon,
  FolderOpen,
  ClipboardList,
  Sparkles,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { makeSuggestionRender } from './suggestionRender'
import type { SuggestionController } from './suggestionController'

export type SlashAction = 'image' | 'qdrive' | 'card' | 'snippet-save' | 'snippet-manage' | 'ai'

export interface SlashItem {
  id: string
  title: string
  hint?: string
  icon: ComponentType<{ className?: string }>
  keywords: string[]
  run?: (editor: Editor, range: Range) => void
  action?: SlashAction
}

export const SLASH_ITEMS: SlashItem[] = [
  { id: 'h1', title: 'Heading 1', hint: 'Large section title', icon: Heading1, keywords: ['h1', 'title', 'heading'], run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 1 }).run() },
  { id: 'h2', title: 'Heading 2', hint: 'Section subtitle', icon: Heading2, keywords: ['h2', 'subtitle', 'heading'], run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 2 }).run() },
  { id: 'bullet', title: 'Bulleted list', hint: 'Simple bullet list', icon: List, keywords: ['ul', 'bullet', 'list', 'unordered'], run: (e, r) => e.chain().focus().deleteRange(r).toggleBulletList().run() },
  { id: 'ordered', title: 'Numbered list', hint: '1. 2. 3.', icon: ListOrdered, keywords: ['ol', 'number', 'ordered', 'list'], run: (e, r) => e.chain().focus().deleteRange(r).toggleOrderedList().run() },
  { id: 'quote', title: 'Quote', hint: 'Block quotation', icon: Quote, keywords: ['quote', 'blockquote'], run: (e, r) => e.chain().focus().deleteRange(r).toggleBlockquote().run() },
  { id: 'divider', title: 'Divider', hint: 'Horizontal rule', icon: Minus, keywords: ['hr', 'divider', 'rule', 'line'], run: (e, r) => e.chain().focus().deleteRange(r).setHorizontalRule().run() },
  { id: 'table', title: 'Table', hint: '3x3 with header row', icon: TableIcon, keywords: ['table', 'grid', 'excel', 'formula'], run: (e, r) => e.chain().focus().deleteRange(r).insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
  { id: 'image', title: 'Image', hint: 'Upload from your device', icon: ImageIcon, keywords: ['image', 'picture', 'photo', 'upload'], action: 'image' },
  { id: 'qdrive', title: 'Image from QDrive', hint: 'Search your org files', icon: FolderOpen, keywords: ['qdrive', 'image', 'file', 'org', 'drive'], action: 'qdrive' },
  { id: 'card', title: 'Kanban card', hint: 'Embed a card chip', icon: ClipboardList, keywords: ['card', 'kanban', 'task', 'board'], action: 'card' },
  { id: 'ai', title: 'Ask AI…', hint: 'Fill a table, draft a formatted email…', icon: Sparkles, keywords: ['ai', 'ask', 'write', 'draft', 'fill', 'compose', 'generate'], action: 'ai' },
]

/**
 * `items` may be a static array or a getter — the getter form lets the host
 * surface live, user-defined snippets (which change at runtime) without
 * rebuilding the whole editor extension list.
 */
export function buildSlash(
  controller: SuggestionController,
  onAction: (action: SlashAction, range: Range) => void,
  items: SlashItem[] | (() => SlashItem[]) = SLASH_ITEMS,
) {
  const getItems = typeof items === 'function' ? items : () => items
  return Extension.create({
    name: 'slashCommands',
    addProseMirrorPlugins() {
      return [
        Suggestion<SlashItem>({
          editor: this.editor,
          char: '/',
          allowSpaces: false,
          startOfLine: false,
          items: ({ query }) => {
            const all = getItems()
            const q = (query || '').toLowerCase().trim()
            if (!q) return all
            return all.filter(
              (it) => it.title.toLowerCase().includes(q) || it.keywords.some((k) => k.includes(q)),
            )
          },
          command: ({ editor, range, props }) => {
            const item = props
            if (item.run) {
              item.run(editor, range)
            } else if (item.action) {
              editor.chain().focus().deleteRange(range).run()
              onAction(item.action, range)
            }
          },
          render: () =>
            makeSuggestionRender<SlashItem>(controller, 'slash', (it) => ({
              id: it.id,
              title: it.title,
              hint: it.hint,
              icon: it.icon,
            })),
        }),
      ]
    },
  })
}
